import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    InputSignal,
    signal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GetSiteStatusLabel, SiteStatusEnum } from '@app/enums/site-status.enum';
import { GetSitesResponse, GetUserDataResponse, SiteControllerv2Service } from '@app/generated-api';
import { BaseSitesListComponent } from '@app/v2/components/base-sites-list/base-sites-list.component';
import { ModalResult } from '@app/v2/enums/modal-result.enum';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { AreSameSets } from '@app/v2/utils/array-functions';
import { effectAllDefined, effectAllDefinedNotNull } from '@app/v2/utils/signal-utils';
import { SiteRegisterModalComponent } from '@components/molecules/modals/site-register-modal/site-register-modal.component';
import { MdsTextFieldComponent } from '@medpacesoftwaredevelopment/designsystem';
import {
    ColDef,
    FilterModel,
    GetQuickFilterTextParams,
    GridState,
    MdsAgGridComponent,
    RowClickedEvent,
} from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';
import { emptyValue } from '@utility/constants';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import { take, tap } from 'rxjs';

@Component({
    selector: 'medpace-sites-list',
    imports: [MdsAgGridComponent, MdsTextFieldComponent],
    templateUrl: './sites-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SitesListComponent extends BaseSitesListComponent<GetSitesResponse> {
    private readonly generatedSiteService = inject(SiteControllerv2Service);
    private readonly router = inject(Router);
    private readonly userService = inject(UserServiceV2);
    private readonly dialog = inject(MatDialog);

    private readonly initialStatusesMatched: WritableSignal<boolean> = signal(false);
    private readonly currentUser: Signal<GetUserDataResponse | undefined> = toSignal(
        this.userService.currentUserData.get$()
    );

    public readonly statuses: InputSignal<number[] | undefined> = input<number[] | undefined>();

    private readonly sitesResource = rxResource({
        stream: () => this.generatedSiteService.siteControllerv2GetSites(),
    });

    protected readonly gridStateStorageKey = 'sites-table-state';

    protected readonly rows: Signal<GetSitesResponse[] | undefined> = this.sitesResource.value;

    protected readonly rowsLoading: Signal<boolean> = computed(
        () => this.sitesResource.isLoading() || !this.sitesResource.hasValue()
    );

    public readonly colDefs: ColDef<GetSitesResponse>[] = [
        {
            field: 'statusId',
            headerName: 'Status',
            valueGetter: (params) => (params.data?.statusId ? GetSiteStatusLabel(params.data.statusId) : emptyValue),
        },
        {
            field: 'sponsor',
            headerName: 'Sponsor Name',
        },
        {
            field: 'protocol',
            headerName: 'Protocol ID',
        },
        {
            field: 'siteNumber',
            headerName: 'Site #',
        },
        {
            field: 'activePrimaryCrcFullName',
            headerName: 'Active Primary CRC',
        },
        {
            field: 'institution',
            headerName: 'Institution Name',
        },
        {
            field: 'piLastName',
            headerName: 'PI Last Name',
        },
        {
            field: 'supportingCrcFullNames',
            hide: true,
            lockVisible: true,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            getQuickFilterText: (params: GetQuickFilterTextParams<GetSitesResponse, string[], any>) =>
                params.value?.join(',') ?? '',
        },
    ];

    constructor() {
        super();

        // set initial statuses in filter
        effectAllDefined(
            {
                initialStateLoaded: this.initialStateLoaded,
                gridApi: this.gridApi,
            },
            (values) => {
                if (!values.initialStateLoaded) {
                    return;
                }
                const statuses = this.statuses();

                if (statuses) {
                    const filterModel: FilterModel = {
                        statusId: {
                            filterType: 'multi',
                            filterModels: [
                                null,
                                {
                                    values: statuses.map(GetSiteStatusLabel),
                                    filterType: 'set',
                                },
                            ],
                        },
                    };
                    values.gridApi.setFilterModel(filterModel);
                }
            }
        );

        // route to dashboard if initial status is different than filter status
        effectAllDefinedNotNull(
            {
                statuses: this.statuses,
                gridState: this.gridState,
                initialStateLoaded: this.initialStateLoaded,
            },
            (values) => {
                if (!values.initialStateLoaded) {
                    return;
                }

                const initStatuses = values.statuses.map(GetSiteStatusLabel);
                const filterStatuses = this.getMultiFilterSetValues(values.gridState.filter?.filterModel, 'statusId');

                if (AreSameSets(initStatuses, filterStatuses)) {
                    this.initialStatusesMatched.set(true);
                    return;
                }

                if (!this.initialStatusesMatched()) {
                    return;
                }

                this.router.navigate(['dashboard'], { replaceUrl: true });
            }
        );
    }

    protected override prepareGridState(gridState: GridState): boolean {
        const data = this.rows();

        if (!data) {
            return false;
        }

        // Check if sites contain any of the statuses saved in state. If not, do not apply statusId filter (happens when last PreRegistered site is registered, and statusId filter had 0 values checked)
        const filterStatuses = this.getMultiFilterSetValues(gridState.filter?.filterModel, 'statusId');

        const anySiteHasAnyFilterStatus = data.some((site) =>
            filterStatuses?.some((status) => status === GetSiteStatusLabel(site.statusId))
        );

        // removes statusId filter if there were any statuses selected, and no site has those statuses
        if ((!filterStatuses || filterStatuses.length > 0) && !anySiteHasAnyFilterStatus) {
            delete gridState.filter?.filterModel?.statusId;
        }

        return true;
    }

    public rowClick(row: RowClickedEvent<GetSitesResponse>): void {
        const user = this.currentUser();
        const site = row.data;
        if (!site || !user) {
            return;
        }

        if (site.statusId === SiteStatusEnum.PreRegistered && site.activePrimaryCrcId === user.id) {
            this.dialog
                .open(SiteRegisterModalComponent, {
                    ...getModalConfig(ModalSize.Medium),
                    data: [site.id],
                })
                .afterClosed()
                .pipe(
                    tap((result: ModalResult) => {
                        if (result === ModalResult.Okay) {
                            this.sitesResource.reload();
                        }
                    }),
                    take(1)
                )
                .subscribe();
        } else {
            const commands: string[] = [`studies`, `${site.studyId}`, `sites`, `${site.id}`];

            this.router.navigate(commands);
        }
    }

    // extracts the values of the 'set' sub-filter nested inside a 'multi' filter model for the given column
    private getMultiFilterSetValues(
        filterModel: FilterModel | null | undefined,
        columnId: string
    ): string[] | undefined {
        const columnFilterModel = filterModel?.[columnId];

        if (!columnFilterModel || columnFilterModel.filterType !== 'multi') {
            return undefined;
        }

        const setFilterModel = columnFilterModel.filterModels?.find(
            (model: { filterType?: string }) => model?.filterType === 'set'
        );

        return setFilterModel?.values;
    }
}
